import { Divider, Grid } from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { CohabitationData } from 'mutation/view-models/declaration-cohabitation';
import { FormikErrors, FormikTouched } from 'formik';
import MunicipalityList from 'shell/components/shared/municipality-list';
import LawyerList from 'shell/components/shared/lawyer-list';
import ProfessionistList from 'shell/components/shared/professionist-select';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { readFileAsDataUrlBase64 } from 'shell/utils/file-to-base64';

interface FormCohabitationDataProps {
  values?: CohabitationData;
  onChange: (key: keyof CohabitationData, value: CohabitationData[keyof CohabitationData]) => void;
  touched?: FormikTouched<CohabitationData>;
  errors?: FormikErrors<CohabitationData>;
}

const FormCohabitationComponent = ({
  values,
  onChange,
  errors,
  touched,
}: FormCohabitationDataProps): React.ReactElement => {
  const handleAddFile = useCallback(
    (file) => {
      readFileAsDataUrlBase64(file).then((newFile) => {
        onChange('file', newFile);
      });
    },
    [onChange]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data richiesta"
          aria-label="Data richiesta"
          value={values?.requestDate || null}
          onChange={(date) => {
            onChange('requestDate', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="requestDate"
              fullWidth
              error={touched?.requestDate && Boolean(errors?.requestDate)}
              helperText={errors?.requestDate}
            />
          )}
        />
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <MunicipalityList
            value={values?.municipality || null}
            onChange={(municipality) => onChange('municipality', municipality)}
            label="Comune"
            id="municipality"
            fromDate={values?.requestDate}
            required
            error={touched?.municipality && !!errors?.municipality}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            label="Provincia"
            aria-label="Provincia"
            id="province"
            fullWidth
            value={values?.municipality?.province || ''}
            disabled
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Contratto</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data stipula"
          aria-label="Data stipula"
          value={values?.stipulationDate || null}
          onChange={(date) => {
            onChange('stipulationDate', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="stipulationDate"
              fullWidth
              error={touched?.stipulationDate && Boolean(errors?.stipulationDate)}
              helperText={errors?.stipulationDate}
            />
          )}
        />
      </Grid>
      <Grid container item xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data notifica"
            aria-label="Data notifica"
            value={values?.notifyDate || null}
            onChange={(date) => {
              onChange('notifyDate', date);
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="notifyDate"
                fullWidth
                error={touched?.notifyDate && Boolean(errors?.notifyDate)}
                helperText={errors?.notifyDate}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <PmiTextField
            label="Protocollo notifica"
            aria-label="Protocollo notifica"
            id="protocol"
            fullWidth
            value={values?.notifyProtocol || ''}
            onChange={(event) => {
              onChange('notifyProtocol', event.target.value);
            }}
            error={touched?.notifyProtocol && Boolean(errors?.notifyProtocol)}
            helperText={errors?.notifyProtocol}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <LawyerList value={values?.list || ''} onChange={(list) => onChange('list', list)} />
        </Grid>
        <Grid item xs={12} md={3}>
          <ProfessionistList
            value={values?.professionist || ''}
            type={values?.list}
            onChange={(professionist) => onChange('professionist', professionist)}
          />
        </Grid>
      </Grid>
      {values?.stipulationDate ||
      values?.notifyDate ||
      values?.notifyProtocol ||
      values?.list ||
      values?.professionist ? (
        <Grid item xs={12}>
          File contratto&nbsp;
          <PmiInputFile
            multiple={false}
            file={values?.file || undefined}
            onChange={handleAddFile}
            color="cdmBrandThird"
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormCohabitationComponent.displayName = 'Form cohabitation  component';

const FormCohabitation = memo(FormCohabitationComponent);

export default FormCohabitation;
